import React, { useMemo, useState } from 'react';
import type {
  CalendarConversationInviteeType,
  CalendarConversationParticipantType,
} from '@axiom/validation';
import { InterviewsStatus } from '@axiom/const';
import {
  Badge,
  CondensedHeader,
  Flashy,
  Gutter,
  Layout,
  LayoutItem,
  Modal,
  ModalHeader,
  ModalSection,
} from '@axiom/ui';

import { InterviewGridAttendeeResponse } from '../InterviewGridAttendeeResponse/InterviewGridAttendeeResponse';

import { InterviewGridAttendeesModalUtil } from './interview-grid-attendees-modal-util';

export const InterviewGridAttendeesModal = ({
  invitees,
  participants,
  status,
}: {
  invitees: CalendarConversationInviteeType[];
  participants: CalendarConversationParticipantType[] | null;
  status: (typeof InterviewsStatus)[keyof typeof InterviewsStatus];
}) => {
  const [openModal, setOpenModal] = useState(false);
  const inviteCount = invitees.length;
  const livePast =
    status === InterviewsStatus.LIVE || status === InterviewsStatus.PAST;

  const inviteeData = useMemo(() => {
    return InterviewGridAttendeesModalUtil.sortAttendees(invitees);
  }, [invitees]);

  const linkText = () => {
    return livePast
      ? 'See who joined'
      : `${inviteeData.acceptedCount} of ${inviteCount} confirmed`;
  };

  return (
    <>
      <a
        data-test="ATTENDEES_CONFIRMATION_LINK"
        href="#"
        onClick={e => {
          e.preventDefault();
          setOpenModal(true);
        }}
      >
        <Flashy name="ATTENDEES_CONFIRMATION_FLASHY" color="brandAxiomOrange">
          {linkText()}
        </Flashy>
      </a>
      {openModal ? (
        <Modal name="ATTENDEES_MODAL">
          <ModalHeader
            name="ATTENDEES_MODAL_HEADER"
            onClose={() => {
              setOpenModal(false);
            }}
          >
            Attendees
          </ModalHeader>
          <ModalSection>
            <Layout direction="vertical">
              {livePast ? (
                <LayoutItem bottomGutter="20px">
                  <CondensedHeader name="JOINED_LABEL">Joined</CondensedHeader>
                  <Gutter bottom="4px" />
                  {participants
                    ? participants.map(participant => {
                        return (
                          <Layout
                            direction="horizontal"
                            position="middle"
                            key={
                              participant.participantName +
                              participant.meetingJoinedAt
                            }
                          >
                            <LayoutItem name="PARTICIPANT_NAME">
                              {participant.participantName}
                            </LayoutItem>
                            <LayoutItem
                              position="right"
                              fluid
                              name="WORKSPACE_PARTICIPANTS_RESPONSE"
                            >
                              <Badge
                                name="PARTICIPANT_JOINED_BADGE"
                                background="green"
                              >
                                JOINED
                              </Badge>
                            </LayoutItem>
                          </Layout>
                        );
                      })
                    : null}
                </LayoutItem>
              ) : null}
              <LayoutItem>
                <Gutter bottom="4px">
                  {livePast ? (
                    <>
                      <CondensedHeader name="INVITED_LABEL">
                        Invited
                      </CondensedHeader>
                      <Gutter bottom="4px" />
                    </>
                  ) : null}
                  <Layout direction="horizontal" position="middle">
                    <LayoutItem rightGutter="8px">
                      <span data-test="CANDIDATE_NAME">
                        {inviteeData.candidate?.calculatedDisplayName ??
                          'Talent'}
                      </span>
                    </LayoutItem>
                    <LayoutItem fluid rightGutter="16px">
                      {inviteeData.candidate?.email ? (
                        <div
                          data-test="CANDIDATE_EMAIL"
                          className="max-lines-strict-1"
                        >
                          ({inviteeData.candidate.email})
                        </div>
                      ) : null}
                    </LayoutItem>
                    <LayoutItem>
                      <InterviewGridAttendeeResponse
                        name="CANDIDATE_ATTENDEE_RESPONSE"
                        data={inviteeData.candidate?.response ?? null}
                      />
                    </LayoutItem>
                  </Layout>
                </Gutter>
              </LayoutItem>
              {[
                ...inviteeData.contactInvitees,
                ...(livePast ? inviteeData.adhocInvitees : []),
              ].map((invitee, idx, list) => {
                return (
                  <LayoutItem
                    key={invitee.email}
                    bottomGutter={idx < list.length - 1 ? '4px' : '0px'}
                  >
                    <Layout direction="horizontal" position="middle">
                      <LayoutItem rightGutter="8px">
                        <span data-test="CLIENT_INVITEE_NAME">
                          {invitee.fullName}
                        </span>
                      </LayoutItem>
                      <LayoutItem fluid rightGutter="16px">
                        <div
                          data-test="CLIENT_INVITEE_EMAIL"
                          className="max-lines-strict-1"
                        >
                          ({invitee.email})
                        </div>
                      </LayoutItem>
                      <LayoutItem>
                        <InterviewGridAttendeeResponse
                          name={`CLIENT_ATTENDEE_RESPONSE_${idx}`}
                          data={invitee.response}
                        />
                      </LayoutItem>
                    </Layout>
                  </LayoutItem>
                );
              })}
              {!livePast && inviteeData.adhocInvitees.length > 0 ? (
                <>
                  <LayoutItem topGutter="16px" bottomGutter="4px">
                    <CondensedHeader name="MANUALLY_ADDED_LABEL">
                      Manually added
                    </CondensedHeader>
                  </LayoutItem>
                  {inviteeData.adhocInvitees.map((invitee, idx, list) => {
                    return (
                      <LayoutItem
                        key={invitee.email}
                        bottomGutter={idx < list.length - 1 ? '4px' : '0px'}
                      >
                        <Layout direction="horizontal" position="middle">
                          <LayoutItem rightGutter="8px">
                            <span
                              data-test="ADHOC_INVITEE_NAME"
                              className="text-nowrap"
                            >
                              {invitee.fullName}
                            </span>
                          </LayoutItem>
                          <LayoutItem fluid rightGutter="16px">
                            <div
                              data-test="ADHOC_INVITEE_EMAIL"
                              className="max-lines-strict-1"
                            >
                              ({invitee.email})
                            </div>
                          </LayoutItem>
                          <LayoutItem>
                            <InterviewGridAttendeeResponse
                              name={`ADHOC_ATTENDEE_RESPONSE_${idx}`}
                              data={invitee.response}
                            />
                          </LayoutItem>
                        </Layout>
                      </LayoutItem>
                    );
                  })}
                </>
              ) : null}
            </Layout>
          </ModalSection>
        </Modal>
      ) : null}
    </>
  );
};
